import { useCallback, useEffect, useState } from "react";
import { Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Field } from "@/components/Field";

const badge = (s) => <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${s === "paid" ? "bg-emerald-500/15 text-emerald-400" : s === "rejected" ? "bg-red-500/15 text-red-400" : "bg-amber-500/15 text-amber-400"}`}>{s.toUpperCase()}</span>;

export const StaffPayouts = ({ earnCoins, onChange }) => {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ coins: "", method: "gpay", number: "", upi: "", account_no: "", holder: "", ifsc: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/staff/me/payouts").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/staff/me/payouts", { coins: Math.round(Number(f.coins) * 3), method: f.method, number: f.number || null, upi: f.upi || null, account_no: f.account_no || null, holder: f.holder || null, ifsc: f.ifsc || null });
      toast.success("പേഔട്ട് അഭ്യർത്ഥന അയച്ചു"); setF({ coins: "", method: "gpay", number: "", upi: "", account_no: "", holder: "", ifsc: "" }); load(); onChange?.();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const METHODS = [["gpay", "GPay നമ്പർ"], ["phonepe", "PhonePe നമ്പർ"], ["paytm", "Paytm നമ്പർ"], ["upi", "UPI ID"], ["bank", "ബാങ്ക് അക്കൗണ്ട്"]];
  return (
    <div data-testid="staff-payouts" className="space-y-3">
      <form onSubmit={submit} className="card rounded-2xl p-4 space-y-3">
        <div className="flex items-center gap-2"><Wallet size={15} className="text-[var(--accent-c)]" /><p className="font-bold text-sm">വരുമാനം വിത്ത്‌ഡ്രോ അഭ്യർത്ഥന</p><span className="text-[11px] text-[var(--muted-text)]">ലഭ്യം: <span className="font-num font-bold text-[var(--accent-c)]">₹{((earnCoins || 0) / 3).toFixed(2)}</span> · കുറഞ്ഞത് ₹100</span></div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="തുക (₹)" testId="payout-coins-input" type="number" min="100" value={f.coins} onChange={(e) => setF({ ...f, coins: e.target.value })} required />
          <div className="space-y-1.5">
            <p className="text-xs text-[var(--rose)]">പേയ്‌മെന്റ് രീതി</p>
            <div className="flex flex-wrap gap-1.5" data-testid="payout-method-group">
              {METHODS.map(([v, l]) => <button key={v} type="button" data-testid={`payout-method-${v}`} onClick={() => setF({ ...f, method: v })} className={`h-9 px-3 rounded-lg text-xs font-semibold border transition-colors ${f.method === v ? "bg-[var(--accent-c)] border-[var(--accent-c)] text-white" : "bg-white border-[var(--line)] text-[var(--muted-text)]"}`}>{l}</button>)}
            </div>
          </div>
        </div>
        {f.method === "bank" ? (
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="അക്കൗണ്ട് നമ്പർ" testId="payout-account-input" value={f.account_no} onChange={(e) => setF({ ...f, account_no: e.target.value })} required />
            <Field label="ഹോൾഡർ പേര്" testId="payout-holder-input" value={f.holder} onChange={(e) => setF({ ...f, holder: e.target.value })} required />
            <Field label="IFSC കോഡ്" testId="payout-ifsc-input" value={f.ifsc} onChange={(e) => setF({ ...f, ifsc: e.target.value })} required placeholder="HDFC0001234" />
          </div>
        ) : f.method === "upi" ? (
          <Field label="UPI ID" testId="payout-upi-input" value={f.upi} onChange={(e) => setF({ ...f, upi: e.target.value })} required placeholder="name@upi" />
        ) : (
          <Field label={`${f.method === "gpay" ? "Google Pay" : f.method === "phonepe" ? "PhonePe" : "Paytm"} നമ്പർ`} testId="payout-number-input" value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} required placeholder="10 അക്ക നമ്പർ" />
        )}
        <button data-testid="payout-submit" disabled={busy} className="btn-accent rounded-full h-11 px-6 text-sm font-bold disabled:opacity-60">അഭ്യർത്ഥിക്കുക</button>
      </form>
      <div className="card rounded-2xl divide-y divide-[var(--line)]" data-testid="staff-payout-list">
        {list.length === 0 && <p className="p-4 text-sm text-[var(--muted-text)]">പേഔട്ട് അഭ്യർത്ഥനകൾ ഇല്ല</p>}
        {list.map((p) => <div key={p.id} className="px-4 py-3 flex items-center justify-between text-sm"><span><span className="font-num font-bold">₹{p.inr}</span> <span className="text-[10px] text-[var(--muted-text)] ml-1">{fmtPayout(p)} · {fmtDate(p.created_at)}</span></span>{badge(p.status)}</div>)}
      </div>
    </div>
  );
};

const fmtPayout = (p) => {
  if (!p.details) return p.upi || "—";
  if (p.details.account_no) return `Bank · ${p.details.holder} · A/C ${p.details.account_no} · ${p.details.ifsc}`;
  if (p.details.upi) return `UPI · ${p.details.upi}`;
  return `${(p.method || "").toUpperCase()} · ${p.details.number}`;
};

export const AdminPayouts = () => {
  const [list, setList] = useState([]);
  const load = useCallback(() => api.get("/admin/payouts").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const act = async (id, a) => { try { await api.post(`/admin/payouts/${id}/${a}`); toast.success(a === "paid" ? "Paid ആയി മാർക്ക് ചെയ്തു" : "നിരസിച്ചു — തുക തിരികെ നൽകി"); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div data-testid="admin-payouts" className="space-y-3">
      <h2 className="text-base md:text-lg font-bold">സ്റ്റാഫ് പേഔട്ട് ലെഡ്ജർ <span className="text-xs text-amber-400 font-num ml-2">{list.filter((p) => p.status === "pending").length} pending</span></h2>
      <div className="card rounded-2xl divide-y divide-[var(--line)]">
        {list.length === 0 && <p className="p-4 text-sm text-[var(--muted-text)]">അഭ്യർത്ഥനകൾ ഇല്ല</p>}
        {list.map((p) => (
          <div key={p.id} data-testid={`payout-row-${p.id}`} className="px-4 py-3 flex flex-wrap items-center gap-3 text-sm">
            <span className="font-semibold w-28">{p.staff_name}</span>
            <span className="font-num font-bold">₹{p.inr}</span>
            <span data-testid={`payout-details-${p.id}`} className="font-num text-xs text-[var(--muted-text)] flex-1 select-text">{fmtPayout(p)} · {fmtDate(p.created_at)}</span>
            {badge(p.status)}
            {p.status === "pending" && <div className="inline-flex gap-1"><button data-testid={`payout-paid-${p.id}`} onClick={() => act(p.id, "paid")} className="btn-accent h-8 px-3 rounded-lg text-xs font-bold">Paid</button><button data-testid={`payout-reject-${p.id}`} onClick={() => act(p.id, "reject")} className="btn-ghost h-8 px-3 rounded-lg text-xs text-red-400">Reject</button></div>}
          </div>
        ))}
      </div>
    </div>
  );
};
